/**
 * Portrait presentation for the classic game screen.
 *
 * Fallout's map and its fixed 640x100 interface bar share one framebuffer.
 * Fitting that whole rectangle to a phone makes both halves needlessly small.
 * While the engine reports that the normal bar is visible, this presenter:
 *
 *   - keeps the real canvas as a centre-cropped, enlarged map surface;
 *   - mirrors the complete bar as one strip below it; and
 *   - maps presses on that mirror back into the original framebuffer.
 *
 * Full-screen engine windows hide the interface bar. At that point the class
 * is removed and the ordinary intact-canvas presentation resumes immediately.
 */

const BAR_HEIGHT = 100;
const PORTRAIT_QUERY = '(max-width: 600px) and (orientation: portrait)';
const CHARACTER_SKILL_NAMES = [
  'Small Guns', 'Big Guns', 'Energy Weapons', 'Unarmed', 'Melee Weapons', 'Throwing',
  'First Aid', 'Doctor', 'Sneak', 'Lockpick', 'Steal', 'Traps', 'Science', 'Repair',
  'Speech', 'Barter', 'Gambling', 'Outdoorsman',
] as const;

/**
 * Labels for the engine's persistent map-cursor modes, indexed by GameMouseMode.
 *
 * Modes 0 (move) and 1 (arrow) are the invisible-by-design defaults and are
 * never announced. Every mode from 2 up replaces the desktop cursor art with
 * something the player is meant to notice, which a phone cannot show at all.
 */
const MOUSE_MODE_LABELS: Record<number, string> = {
  2: 'Attack',
  3: 'Use item on…',
  4: 'First Aid',
  5: 'Doctor',
  6: 'Lockpick',
  7: 'Steal',
  8: 'Traps',
  9: 'Science',
  10: 'Repair',
};
const MOUSE_MODE_HINT = 'tap a target · tap ground to cancel';

interface Part {
  canvas: HTMLCanvasElement;
  x: number;
  width: number;
}

export interface MobilePresentation {
  active(): boolean;
  fit(gameWidth: number, gameHeight: number): boolean;
  transformMap(
    factor: number,
    previousClientX: number,
    previousClientY: number,
    clientX: number,
    clientY: number
  ): boolean;
  destroy(): void;
}

export function mountMobilePresentation(
  source: HTMLCanvasElement,
  portraitMode: () => number,
  pointerEvent: (normalizedX: number, normalizedY: number, buttons: number) => void,
  inputModality: (touchLike: boolean) => void,
  inCombat: () => boolean,
  indicatorCount: () => number,
  mouseMode: () => number,
  isTouchInput: () => boolean,
  navigationEvent: (target: number) => void,
  characterSkillState: () => { active: boolean; index: number; value: number; points: number },
  characterSkillAdjust: (delta: number) => void,
  onLayoutChange: () => void
): MobilePresentation {
  const presentation = document.querySelector<HTMLElement>('#game-presentation')!;
  const worldClip = document.querySelector<HTMLElement>('#world-clip')!;
  const statusBar = document.querySelector<HTMLCanvasElement>('#mobile-status-bar')!;
  const characterControls = document.querySelector<HTMLElement>('#mobile-character-controls')!;
  const characterSkillName = document.querySelector<HTMLElement>('#character-skill-name')!;
  const characterSkillValue = document.querySelector<HTMLElement>('#character-skill-value')!;
  const characterSkillMinus = document.querySelector<HTMLButtonElement>('#character-skill-minus')!;
  const characterSkillPlus = document.querySelector<HTMLButtonElement>('#character-skill-plus')!;
  const media = matchMedia(PORTRAIT_QUERY);
  // The cursor-mode badge is shell chrome with no markup of its own, so it is
  // built here rather than in index.html. It reports engine state only; the
  // stylesheet keeps it out of the way of pointer events entirely.
  const cursorBadge = document.createElement('div');
  cursorBadge.id = 'mobile-cursor-mode';
  cursorBadge.setAttribute('role', 'status');
  cursorBadge.hidden = true;
  const cursorBadgeLabel = document.createElement('strong');
  const cursorBadgeHint = document.createElement('span');
  cursorBadge.append(cursorBadgeLabel, cursorBadgeHint);
  presentation.append(cursorBadge);
  const parts: Part[] = [
    { canvas: document.querySelector<HTMLCanvasElement>('#mobile-hud-bar')!, x: 0, width: 640 },
  ];

  let isActive = false;
  let mode = 0;
  let gameWidth = 640;
  let gameHeight = 480;
  let mapZoom = 1;
  let mapPanX = 0;
  let mapPanY = 0;
  let frame = 0;

  const setMode = (nextMode: number) => {
    const next = media.matches && (nextMode === 1 || nextMode === 2);
    const nextMap = next && nextMode === 1;
    const changed = next !== isActive || nextMode !== mode;
    if (changed && mode === 1 && (nextMode !== 1 || !next)) {
      // The mirrored HUD drives an engine-side synthetic mouse. Release that
      // ownership before a stock modal or inventory resumes native gestures.
      pointerEvent(0, 0, -1);
    }
    isActive = next;
    mode = next ? nextMode : 0;
    presentation.classList.toggle('mobile-hud-active', next);
    presentation.classList.toggle('mobile-map-active', nextMap);
    if (!nextMap) source.style.removeProperty('clip-path');
    if (changed) requestAnimationFrame(onLayoutChange);
  };

  const syncMirrorSizes = (bufferScale: number) => {
    for (const part of parts) {
      const width = Math.max(1, Math.round(part.width * bufferScale));
      const height = Math.max(1, Math.round(BAR_HEIGHT * bufferScale));
      if (part.canvas.width !== width) part.canvas.width = width;
      if (part.canvas.height !== height) part.canvas.height = height;
    }
  };

  const paint = () => {
    const bufferScale = source.width / gameWidth;
    if (!Number.isFinite(bufferScale) || bufferScale <= 0) return;
    syncMirrorSizes(bufferScale);
    const barX = Math.max(0, (gameWidth - 640) / 2);
    const barY = Math.max(0, gameHeight - BAR_HEIGHT);
    for (const part of parts) {
      const ctx = part.canvas.getContext('2d');
      if (!ctx) continue;
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(
        source,
        Math.round((barX + part.x) * bufferScale),
        Math.round(barY * bufferScale),
        Math.round(part.width * bufferScale),
        Math.round(BAR_HEIGHT * bufferScale),
        0,
        0,
        part.canvas.width,
        part.canvas.height
      );
    }
  };

  const paintStatus = (bufferScale: number) => {
    const count = Math.max(0, Math.min(16, indicatorCount()));
    if (mode !== 1 || count === 0) {
      statusBar.hidden = true;
      return;
    }

    const clipWidth = worldClip.clientWidth;
    if (clipWidth < 1) return;
    const logicalWidth = 127 * count;
    const displayScale = Math.min(0.65, Math.max(0.25, (clipWidth - 16) / logicalWidth));
    const cssHeight = Math.max(8, 21 * displayScale);
    const dpr = window.devicePixelRatio || 1;
    statusBar.width = Math.max(1, Math.round(clipWidth * dpr));
    statusBar.height = Math.max(1, Math.round(cssHeight * dpr));
    statusBar.style.height = `${cssHeight}px`;
    statusBar.hidden = false;

    const ctx = statusBar.getContext('2d');
    if (!ctx) return;
    ctx.imageSmoothingEnabled = false;
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, statusBar.width, statusBar.height);
    const sourceWidth = logicalWidth * bufferScale;
    const sourceHeight = 21 * bufferScale;
    const sourceX = ((gameWidth - logicalWidth) / 2) * bufferScale;
    const sourceY = (gameHeight - BAR_HEIGHT - 21) * bufferScale;
    ctx.drawImage(
      source,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      8 * dpr,
      0,
      logicalWidth * displayScale * dpr,
      cssHeight * dpr
    );
  };

  // Only rewrite the badge when the mode actually changes. Every frame runs
  // through here, and unchanged text still costs a style and layout pass.
  let paintedCursorMode = -1;

  const paintCursorBadge = () => {
    // Touch play has no floating cursor to carry the mode, so the badge is the
    // only cue there is. It is equally needed in landscape, hence no dependence
    // on the portrait presentation being active - only on the modality and on
    // the game screen being the one on show.
    const cursorMode = isTouchInput() && document.body.classList.contains('playing') ? mouseMode() : 0;
    const label = MOUSE_MODE_LABELS[cursorMode];
    if (cursorMode === paintedCursorMode) return;
    paintedCursorMode = cursorMode;
    cursorBadge.hidden = !label;
    if (!label) return;
    cursorBadgeLabel.textContent = label;
    // Attack is self-explanatory and permanent until the player leaves it. The
    // use and skill modes are the ones whose exit gesture is not discoverable.
    cursorBadgeHint.textContent = cursorMode >= 3 ? MOUSE_MODE_HINT : '';
    cursorBadgeHint.hidden = cursorMode < 3;
  };

  const tick = () => {
    setMode(portraitMode());
    paintCursorBadge();
    const characterState = characterSkillState();
    const showCharacterControls = media.matches && characterState.active;
    presentation.classList.toggle('character-editor-active', showCharacterControls);
    characterControls.hidden = !showCharacterControls;
    if (showCharacterControls) {
      const skillSelected = characterState.index >= 0;
      characterSkillMinus.disabled = !skillSelected;
      characterSkillPlus.disabled = !skillSelected;
      characterSkillName.textContent = CHARACTER_SKILL_NAMES[characterState.index] ?? 'Selected skill';
      characterSkillValue.textContent = characterState.value >= 0 && characterState.points >= 0
        ? `${characterState.value}% · ${characterState.points} point${characterState.points === 1 ? '' : 's'} left`
        : 'Tap a skill row first';
    }
    // Freeze the last stock HUD frame under inventory. Modal pixels may cover
    // the source framebuffer's bar, but the persistent navigator must not.
    if (mode === 1) {
      paint();
      paintStatus(source.width / gameWidth);
    } else {
      statusBar.hidden = true;
    }
    frame = requestAnimationFrame(tick);
  };

  const logicalPoint = (part: Part, event: PointerEvent) => {
    const rect = part.canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    const barX = Math.max(0, (gameWidth - 640) / 2);
    const x = barX + part.x + ((event.clientX - rect.left) / rect.width) * part.width;
    const y = gameHeight - BAR_HEIGHT + ((event.clientY - rect.top) / rect.height) * BAR_HEIGHT;
    return { x, y, barX };
  };

  const navigationTarget = (x: number, y: number): number => {
    if (x >= 195 && x <= 255) {
      if (y >= 34 && y < 63) return 1; // Inventory.
      if (y >= 63) return 6; // Options.
    }
    if (x >= 500) {
      if (y < 34) return 4; // Skilldex.
      if (y < 58) return 5; // Automap.
      if (y < 78) return 3; // Character.
      return 2; // Pip-Boy.
    }
    return 0;
  };

  const semanticHudTarget = (part: Part, event: PointerEvent): number => {
    if (mode !== 1) return 0;
    const point = logicalPoint(part, event);
    if (!point) return 0;
    const x = point.x - point.barX;
    const y = point.y - (gameHeight - BAR_HEIGHT);
    // The combat buttons share the same far-right strip as the navigation
    // labels but occupy their own two lower rows. Resolve them first.
    if (x >= 570 && y >= 32 && y < 96) {
      return y < 64 ? 7 : 8; // End Turn / End Combat.
    }
    return navigationTarget(x, y);
  };

  const isWeaponActionButton = (part: Part, event: PointerEvent): boolean => {
    if (mode !== 1) return false;
    const point = logicalPoint(part, event);
    if (!point) return false;
    const x = point.x - point.barX;
    const y = point.y - (gameHeight - BAR_HEIGHT);
    return x >= 267 && x < 455 && y >= 26 && y < 93;
  };

  const route = (part: Part, event: PointerEvent, buttons: number) => {
    const point = logicalPoint(part, event);
    if (!point) return;
    if (mode === 2) {
      if (buttons === 0) {
        const target = navigationTarget(point.x - point.barX, point.y - (gameHeight - BAR_HEIGHT));
        if (target) navigationEvent(target);
      }
      return;
    }
    let x = point.x;
    let y = point.y;
    if (mode === 1 && event.pointerType === 'touch') {
      const localX = x - point.barX;
      const localY = y - (gameHeight - BAR_HEIGHT);
      if (localX >= 570 && localY >= 32 && localY < 96) {
        // The stock End Turn / End Combat controls are only 38x22 pixels.
        // Give each the full right-hand row and snap to its real button centre.
        x = point.barX + 609;
        y = gameHeight - BAR_HEIGHT + (localY < 64 ? 54 : 76);
      }
    }
    pointerEvent(
      Math.round((x / gameWidth) * 1_000_000),
      Math.round((y / gameHeight) * 1_000_000),
      buttons
    );
  };

  const cleanups: Array<() => void> = [];
  const bindCharacterAdjust = (selector: string, delta: number) => {
    const button = document.querySelector<HTMLButtonElement>(selector)!;
    let delayTimer: number | undefined;
    let repeatTimer: number | undefined;
    const stop = () => {
      window.clearTimeout(delayTimer);
      window.clearInterval(repeatTimer);
      delayTimer = undefined;
      repeatTimer = undefined;
      button.classList.remove('pressed');
    };
    const down = (event: PointerEvent) => {
      const state = characterSkillState();
      if (!media.matches || !state.active || state.index < 0) return;
      event.preventDefault();
      stop();
      inputModality(event.pointerType !== 'mouse');
      button.setPointerCapture(event.pointerId);
      button.classList.add('pressed');
      characterSkillAdjust(delta);
      delayTimer = window.setTimeout(() => {
        repeatTimer = window.setInterval(() => characterSkillAdjust(delta), 130);
      }, 420);
    };
    button.addEventListener('pointerdown', down);
    button.addEventListener('pointerup', stop);
    button.addEventListener('pointercancel', stop);
    button.addEventListener('lostpointercapture', stop);
    cleanups.push(() => {
      stop();
      button.removeEventListener('pointerdown', down);
      button.removeEventListener('pointerup', stop);
      button.removeEventListener('pointercancel', stop);
      button.removeEventListener('lostpointercapture', stop);
    });
  };
  bindCharacterAdjust('#character-skill-minus', -1);
  bindCharacterAdjust('#character-skill-plus', 1);

  for (const part of parts) {
    let pressedAt = 0;
    let pressedX = 0;
    let pressedY = 0;
    let weaponPress = false;
    let weaponPressInCombat = false;
    // Set when finger drift kills a weapon-panel gesture. The rest of that
    // contact must be inert: converting it into a live left-button drag (the
    // old behavior) made the engine cursor leap across the HUD and could press
    // whatever button the finger drifted over.
    let weaponCancelled = false;
    let semanticTarget = 0;
    let longPressTriggered = false;
    let longPressTimer: number | undefined;
    let releaseTimer: number | undefined;
    let pendingRelease: (() => void) | undefined;
    const scheduleRelease = (release: () => void, delay: number) => {
      flushRelease();
      pendingRelease = release;
      releaseTimer = window.setTimeout(() => {
        releaseTimer = undefined;
        pendingRelease = undefined;
        release();
      }, delay);
    };
    // Run a pending release now instead of losing it. The mirrored HUD drives
    // one synthetic engine pointer, so a dropped release leaves that pointer
    // stuck at buttons=1 and every later press lands on a button already held.
    const flushRelease = () => {
      if (releaseTimer === undefined) return;
      window.clearTimeout(releaseTimer);
      releaseTimer = undefined;
      const release = pendingRelease;
      pendingRelease = undefined;
      release?.();
    };
    const cancelRelease = () => {
      window.clearTimeout(releaseTimer);
      releaseTimer = undefined;
      pendingRelease = undefined;
    };
    const down = (event: PointerEvent) => {
      event.preventDefault();
      inputModality(event.pointerType !== 'mouse');
      window.clearTimeout(longPressTimer);
      // The semantic-target and weapon-press paths below return without ever
      // sending this press's release, so a still-pending one from the previous
      // press has to be delivered before that happens, not merely cancelled.
      flushRelease();
      pressedAt = performance.now();
      pressedX = event.clientX;
      pressedY = event.clientY;
      weaponPress = isWeaponActionButton(part, event);
      weaponPressInCombat = weaponPress && inCombat();
      weaponCancelled = false;
      semanticTarget = semanticHudTarget(part, event);
      longPressTriggered = false;
      part.canvas.setPointerCapture(event.pointerId);
      // These controls are persistent mobile navigation, not map gestures.
      // Own the complete contact and dispatch one semantic event on release;
      // never feed its down/up pair through the legacy mouse hit tester where
      // a mode change can turn the release into an accidental world tap.
      if (semanticTarget) return;
      if (weaponPress) {
        // Fallout uses right-click on this panel to cycle Normal, Aimed,
        // alternate fire, and Reload. A hold supplies that missing one-finger
        // gesture without changing the stock panel or its short-tap action.
        // 650ms, not 450: in combat a hold EXECUTES the displayed action, and
        // at 450 a deliberate-but-unhurried tap crossed the line and fired the
        // weapon (or spammed "Out of ammo.") when the player only meant to
        // cycle toward Reload. 650 matches the touch double-tap window, the
        // one-thumb timing the rest of the game already uses.
        longPressTimer = window.setTimeout(() => {
          longPressTriggered = true;
          // In combat, hold executes the displayed action (notably Reload).
          // Outside combat it retains the old hold-to-cycle shortcut.
          route(part, event, weaponPressInCombat ? 1 : 2);
          scheduleRelease(() => {
            route(part, event, 0);
            // Give the synthetic pointer back so the cursor does not stay
            // parked on the weapon box until the next map touch claims it.
            pointerEvent(0, 0, -1);
          }, 70);
        }, 650);
        return;
      }
      route(part, event, 1);
    };
    const move = (event: PointerEvent) => {
      if (!part.canvas.hasPointerCapture(event.pointerId)) return;
      event.preventDefault();
      inputModality(event.pointerType !== 'mouse');
      if (semanticTarget) return;
      if (weaponPress) {
        if (Math.hypot(event.clientX - pressedX, event.clientY - pressedY) > 12) {
          // Drift kills the gesture outright. No press was ever sent for it,
          // so there is nothing to release - and it must NOT fall through to
          // the live left-drag below, which would sweep a held button across
          // the HUD.
          window.clearTimeout(longPressTimer);
          weaponPress = false;
          weaponCancelled = true;
        }
        return;
      }
      if (weaponCancelled) return;
      route(part, event, 1);
    };
    const up = (event: PointerEvent) => {
      event.preventDefault();
      if (part.canvas.hasPointerCapture(event.pointerId)) part.canvas.releasePointerCapture(event.pointerId);
      window.clearTimeout(longPressTimer);
      // A pending release from the long-press path would double-fire alongside
      // the release this handler sends; only one button-up may reach the engine.
      cancelRelease();
      if (semanticTarget) {
        navigationEvent(semanticTarget);
        semanticTarget = 0;
        weaponPress = false;
        weaponPressInCombat = false;
        return;
      }
      if (weaponCancelled) {
        // The drifted contact sent no press; its release must send nothing.
        weaponCancelled = false;
        return;
      }
      if (weaponPress) {
        if (!longPressTriggered) {
          // Direct hostile taps already enter targeting during combat, freeing
          // a short weapon-panel tap to cycle its stock right-click modes.
          route(part, event, weaponPressInCombat ? 2 : 1);
          scheduleRelease(() => {
            route(part, event, 0);
            pointerEvent(0, 0, -1);
          }, 50);
        } else {
          route(part, event, 0);
          pointerEvent(0, 0, -1);
        }
        weaponPress = false;
        weaponPressInCombat = false;
        return;
      }
      // A browser click can deliver down/up inside one engine frame. Keep the
      // button down long enough for SDL's polling loop to observe it once.
      const wait = Math.max(0, 50 - (performance.now() - pressedAt));
      scheduleRelease(() => route(part, event, 0), wait);
    };
    const cancel = (event: PointerEvent) => {
      window.clearTimeout(longPressTimer);
      cancelRelease();
      weaponPress = false;
      weaponPressInCombat = false;
      weaponCancelled = false;
      if (semanticTarget) {
        semanticTarget = 0;
        return;
      }
      semanticTarget = 0;
      route(part, event, 0);
    };
    const contextMenu = (event: MouseEvent) => event.preventDefault();
    part.canvas.addEventListener('pointerdown', down);
    part.canvas.addEventListener('pointermove', move);
    part.canvas.addEventListener('pointerup', up);
    part.canvas.addEventListener('pointercancel', cancel);
    part.canvas.addEventListener('contextmenu', contextMenu);
    cleanups.push(() => {
      window.clearTimeout(longPressTimer);
      // Flush, don't just clear: unmounting with a release pending would drop
      // it and leave the mirrored HUD's synthetic engine pointer stuck at
      // buttons=1 - exactly the stuck-button state the release timer exists to
      // prevent.
      flushRelease();
      part.canvas.removeEventListener('pointerdown', down);
      part.canvas.removeEventListener('pointermove', move);
      part.canvas.removeEventListener('pointerup', up);
      part.canvas.removeEventListener('pointercancel', cancel);
      part.canvas.removeEventListener('contextmenu', contextMenu);
    });
  }

  frame = requestAnimationFrame(tick);

  return {
    active: () => isActive,
    fit(nextGameWidth: number, nextGameHeight: number) {
      gameWidth = nextGameWidth;
      gameHeight = nextGameHeight;
      if (!isActive) return false;

      const clipWidth = worldClip.clientWidth;
      const clipHeight = worldClip.clientHeight;
      const worldHeight = Math.max(1, gameHeight - BAR_HEIGHT);
      if (clipWidth < 1 || clipHeight < 1) return true;

      const zoom = mode === 1
        // Map presentation is a true cover fit of the rendered world. Pinch
        // multiplies that fit but can never shrink below it, so no framebuffer
        // edge or letterbox can enter the world pane.
        ? Math.max(clipWidth / gameWidth, clipHeight / worldHeight) * mapZoom
        // Inventory's stock window occupies roughly the centered 540 pixels of
        // a 640-wide frame. Keep a safe margin around it without shrinking the
        // useful UI as much as fitting the empty outer edges would.
        : Math.min(clipWidth / Math.min(gameWidth, 540), clipHeight / worldHeight);
      const width = gameWidth * zoom;
      const height = gameHeight * zoom;
      source.style.width = `${width}px`;
      source.style.height = `${height}px`;
      const worldDisplayHeight = worldHeight * zoom;
      const centeredLeft = (clipWidth - width) / 2;
      const centeredTop = (clipHeight - worldDisplayHeight) / 2;
      const left = mode === 1
        ? Math.min(0, Math.max(clipWidth - width, centeredLeft + mapPanX))
        : centeredLeft;
      const top = mode === 1
        ? Math.min(0, Math.max(clipHeight - worldDisplayHeight, centeredTop + mapPanY))
        : centeredTop;
      // Save the clamped offset. This prevents accumulated off-screen pan from
      // making the map jump when a later zoom exposes room in that direction.
      if (mode === 1) {
        mapPanX = left - centeredLeft;
        mapPanY = top - centeredTop;
      }
      source.style.left = `${left}px`;
      source.style.top = mode === 1
        ? `${top}px`
        // The inventory window belongs in the center of the available pane.
        // CSS clips the original framebuffer HUD; the cached stock bar remains
        // the only copy below the pane.
        : `${(clipHeight - worldHeight * zoom) / 2}px`;
      if (mode === 1) {
        // The stock HUD is physically part of the same framebuffer as the
        // map. Pinch-zooming the source previously pulled that original bar
        // into view underneath the fixed mobile mirror, producing two HUDs.
        // Always clip the framebuffer's bottom BAR_HEIGHT pixels; zoom is
        // allowed to affect only the map viewport.
        source.style.clipPath = `inset(0 0 ${(BAR_HEIGHT / gameHeight) * 100}% 0)`;
      } else {
        source.style.removeProperty('clip-path');
      }
      return true;
    },
    transformMap(factor, previousClientX, previousClientY, clientX, clientY) {
      if (!isActive || mode !== 1 || !Number.isFinite(factor) || factor <= 0) return false;
      const next = Math.min(3, Math.max(1, mapZoom * factor));
      const appliedFactor = next / mapZoom;
      const clipRect = worldClip.getBoundingClientRect();
      const previousX = previousClientX - clipRect.left;
      const previousY = previousClientY - clipRect.top;
      const currentX = clientX - clipRect.left;
      const currentY = clientY - clipRect.top;
      const worldHeight = Math.max(1, gameHeight - BAR_HEIGHT);
      const coverZoom = Math.max(clipRect.width / gameWidth, clipRect.height / worldHeight);
      // Derive this from presentation state rather than the DOM. Pointermove
      // can fire several times before the next animation frame applies styles.
      const oldWidth = gameWidth * coverZoom * mapZoom;
      const oldWorldHeight = worldHeight * coverZoom * mapZoom;
      const oldLeft = Math.min(0, Math.max(clipRect.width - oldWidth, (clipRect.width - oldWidth) / 2 + mapPanX));
      const oldTop = Math.min(0, Math.max(clipRect.height - oldWorldHeight, (clipRect.height - oldWorldHeight) / 2 + mapPanY));
      const nextWidth = gameWidth * coverZoom * next;
      const nextWorldHeight = worldHeight * coverZoom * next;
      const desiredLeft = currentX - (previousX - oldLeft) * appliedFactor;
      const desiredTop = currentY - (previousY - oldTop) * appliedFactor;
      mapZoom = next;
      mapPanX = desiredLeft - (clipRect.width - nextWidth) / 2;
      mapPanY = desiredTop - (clipRect.height - nextWorldHeight) / 2;
      requestAnimationFrame(onLayoutChange);
      return true;
    },
    destroy() {
      cancelAnimationFrame(frame);
      cleanups.forEach((cleanup) => cleanup());
      presentation.classList.remove('mobile-hud-active');
      presentation.classList.remove('mobile-map-active');
      presentation.classList.remove('character-editor-active');
      characterControls.hidden = true;
      cursorBadge.remove();
      source.style.removeProperty('clip-path');
    },
  };
}
